import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";

import "../../styles/marketing.css";
import Seo, { breadcrumbJsonLd, SITE_URL } from "../../components/marketing/Seo";
import MarketingNav from "../../components/marketing/MarketingNav";
import MarketingFooter from "../../components/marketing/MarketingFooter";
import CtaBanner from "../../components/marketing/CtaBanner";
import Reveal from "../../components/marketing/Reveal";
import { BLOG_POSTS } from "../../data/blogData";

const fmtDate = (d) =>
  new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const PostCard = ({ post, featured = false }) => (
  <Link
    to={`/blog/${post.slug}`}
    className={`group block rounded-2xl border border-slate-200 bg-white overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_16px_32px_-12px_rgba(15,23,42,0.16)] hover:border-blue-200 ${featured ? "md:grid md:grid-cols-2" : ""}`}
    data-testid={`blog-card-${post.slug}`}
  >
    <div className={`overflow-hidden ${featured ? "md:h-full" : "h-44"} bg-slate-100`}>
      <img
        src={post.img}
        alt={post.imgAlt}
        loading="lazy"
        className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.03]"
      />
    </div>
    <div className={`p-6 ${featured ? "md:p-8 flex flex-col justify-center" : ""}`}>
      <div className="flex items-center gap-3 mb-3">
        <span className="px-2.5 py-1 rounded-full bg-[var(--mk-blue-light)] text-[var(--mk-blue)] text-[11px] font-bold uppercase tracking-wide">{post.category}</span>
        <span className="flex items-center gap-1 text-[12.5px] text-slate-400 font-medium">
          <Clock className="w-3.5 h-3.5" /> {post.readMins} min read
        </span>
      </div>
      <h2 className={`font-bold tracking-tight text-slate-900 mb-2.5 leading-snug ${featured ? "text-2xl" : "text-[18px]"}`}>{post.title}</h2>
      <p className={`text-slate-500 leading-relaxed mb-4 ${featured ? "text-[15px]" : "text-[14px] line-clamp-2"}`}>{post.excerpt}</p>
      <div className="flex items-center justify-between">
        <span className="text-[13px] text-slate-400">{fmtDate(post.date)}</span>
        <span className="inline-flex items-center gap-1 text-[14px] font-semibold text-[var(--mk-blue)]">
          Read article <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </div>
  </Link>
);

export default function BlogIndexPage() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Blog", path: "/blog" },
  ];
  const [featured, ...rest] = BLOG_POSTS;

  return (
    <div className="mk min-h-screen overflow-x-hidden" data-testid="blog-index-page">
      <Seo
        title="Fieldz AI Blog — Field Service Business Guides & Playbooks"
        description="Practical guides for field service businesses: reducing no-shows, KPIs that matter, call masking, getting paid faster, multi-location dispatch, and choosing software."
        path="/blog"
        image={`${SITE_URL}/marketing/og/og-blog.jpg`}
        jsonLd={[
          breadcrumbJsonLd(crumbs),
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Fieldz AI Blog",
            url: `${SITE_URL}/blog`,
            description: "Practical guides and playbooks for field service businesses.",
          },
        ]}
      />
      <MarketingNav />

      <section className="pt-28 md:pt-36 pb-12 bg-gradient-to-b from-blue-50/70 via-white to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[var(--mk-blue)] mb-5">Blog</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-5">
            Grow your field service business
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Practical playbooks on operations, money, and communication — written for the people running the trucks.
          </p>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-8">
            <PostCard post={featured} featured />
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {rest.map((p, i) => (
              <Reveal key={p.slug} delay={Math.min((i % 3) + 1, 3)}>
                <PostCard post={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner title="Put these playbooks on autopilot" body="Most of what we write about is one toggle away inside Fieldz AI. Try it free for 7 days." />
      <MarketingFooter />
    </div>
  );
}
